import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FieldShell } from '@/components/forms/FieldShell';
import { FormFieldRenderer, type FieldMeta, type EnumOption } from '@/components/forms/FormFieldRenderer';
import { SourceField, type SourceSettings } from '@/components/forms/SourceField';
import { ReferencePickerField } from '@/components/forms/ReferencePickerField';
import { AddressField } from '@/components/forms/AddressField';
import { AssigneePicker } from '@/components/autoforms/AssigneePicker';
import { classifyFieldKind, type FieldKindInput } from '@/lib/formFieldKind';
import { matchExtensionForField } from '@/lib/fieldExtensions';
import { applyFormats, type CondFormat, type CondContext } from '@/lib/conditionalFormat';
import { getSDK } from '@/store/sdkStore';
import { friendlyError } from '@/lib/errors';
import { isAuditField } from '@/lib/auditFields';

// ============================================================================
// AutoFormField — рендер ОДНОГО реквизита АВТО-формы по «виду» (вынесено из
// AutoObjectForm.renderField, E2e10): классификатор (formFieldKind), условное
// оформление, ветки anyref/assignee/address/reference/source/default.
// ЧИСТО отображательный компонент (SRP): значения и ВСЕ записи остаются в
// AutoObjectForm (formStore/стор) — сюда передаются данные и колбэки.
// ============================================================================

export interface AutoFormFieldDef {
  id?: string;
  code?: string;
  name?: string;
  synonym?: string;
  dataType?: string;
  data_type?: string;
  isRequired?: boolean;
  is_required?: boolean;
  order?: number;
  refTable?: string;
  ref_table?: string;
  refCatalogCode?: string;
  hiddenByDefault?: boolean;
  hidden_by_default?: boolean;
  types?: string[];
  sourceSettings?: string;
  source_settings?: string;
  voiceInput?: boolean;
  voice_input?: boolean;
  hint?: string;
  hintPosition?: string;
  hint_position?: string;
  selectionParams?: Array<{ field: string; condition: string; value?: string }>;
  selectionLinks?: Array<{ param: string; field: string }>;
}

interface AutoFormFieldProps {
  field: AutoFormFieldDef;
  /** тип родителя (task — признак поля «Исполнитель») */
  objectType: string;
  objectName: string;
  data: Record<string, any>;
  onDataChange: (updater: (prev: Record<string, any>) => Record<string, any>) => void;
  fieldErrors: Record<string, string>;
  /** ТОЛЬКО ЧТЕНИЕ (форма целиком): существующая задача — поля не редактируются
   *  (реквизиты меняются только командами/статусами). */
  readOnly?: boolean;
  attrSelectModes: Record<string, string>;
  enumOptions: Record<string, EnumOption[]>;
  /** Значения-переопределения селекта ПОЛЯ (напр. «Статус» задачи — из статусной
   *  схемы): приоритетнее enumOptions[refTable]. */
  options?: EnumOption[];
  extReady: boolean;
  condRules: CondFormat[];
  condStyles: Record<string, any>;
  condCtx: CondContext;
  statusFieldName?: string;
  onSaveError: (msg: string) => void;
  openAnyRefPicker: (field: string, label: string) => void;
  clearFieldError: (code: string) => void;
}

export function AutoFormField({
  field, objectType, objectName, data, onDataChange, fieldErrors, readOnly, attrSelectModes,
  enumOptions, options: optionsOverride, extReady, condRules, condStyles, condCtx, statusFieldName,
  onSaveError, openAnyRefPicker, clearFieldError,
}: AutoFormFieldProps) {
  const code = field.code || field.name || '';
  if (!code) return null;
  const dt = (field.dataType || field.data_type || 'text').toLowerCase();
  const refTable = field.refTable || field.ref_table || field.refCatalogCode || '';
  const err = fieldErrors[code];
  const label = field.synonym || field.name || field.code || '';
  // ЕДИНЫЙ классификатор (SRP, formFieldKind.ts): снижает ветвление (цикломатика).
  const addrExt = extReady ? matchExtensionForField(field as any) : null;
  const sourceRaw = String(field.sourceSettings || field.source_settings || '');
  const kind = classifyFieldKind({
    dataType: dt,
    refTable,
    isAddress: !!(addrExt && addrExt.code === 'address'),
    isAssignee: objectType === 'task' && (code === 'assignee_id' || code === 'assignee'),
    sourceSettings: sourceRaw,
  } satisfies FieldKindInput);

  // Условное оформление (как в 1С): скрыть/заблокировать/стиль поля
  const fmt = applyFormats(condRules, { ...data, status: data[statusFieldName || 'status'] }, condCtx, `field:${code}`);
  if (fmt.hidden) return null;
  // СИСТЕМНЫЕ аудит-поля (Создал/Дата создания/Изменил/Дата изменения):
  // никогда не редактируются (значения заполняет сервер) — рендерим disabled.
  const auditLocked = isAuditField(code);
  const fmtStyle = fmt.styleId ? condStyles[fmt.styleId] : undefined;
  const required = !!(field.isRequired || field.is_required);
  const fieldDisabled = fmt.disabled || auditLocked || !!readOnly;

  const setValue = (updates: Record<string, unknown>, clearError = true) => {
    onDataChange((prev) => ({ ...prev, ...updates }));
    if (clearError) clearFieldError(code);
  };

  if (kind === 'anyref') {
    return (
      <FieldShell key={code} label={label} labelStyle={fmtStyle} error={err} required={required}>
        <div className="flex items-center gap-1">
          <Input
            value={String(data[code + '_presentation'] ?? data[code] ?? '')}
            readOnly
            placeholder="Не выбрано"
            className="h-8 text-sm flex-1"
            disabled={fieldDisabled}
          />
          <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={() => openAnyRefPicker(code, label)} disabled={fieldDisabled}>…</Button>
          {data[code] ? (
            <Button size="sm" variant="ghost" className="h-8 text-[11px]" onClick={() => setValue({ [code]: '', [code + '_table']: '', [code + '_presentation']: '' })}>✕</Button>
          ) : null}
        </div>
      </FieldShell>
    );
  }
  // «Исполнитель» задачи (assignee_id, reference → entities) — специализированный
  // выбор с загруженностью (SSoT — SuggestAssignees, роль «Исполнитель», бейдж
  // «N активных задач», свободные выше). Включаемый механизм ядра.
  if (kind === 'assignee') {
    return (
      <FieldShell key={code} label={label} labelStyle={fmtStyle} error={err} required={required}>
        <AssigneePicker
          value={String(data[code] ?? '')}
          displayValue={String(data[code + '_presentation'] ?? '')}
          taskType={objectName}
          disabled={fieldDisabled}
          required={required}
          onChange={(v) => setValue({ [code]: v })}
        />
      </FieldShell>
    );
  }
  if (kind === 'address') {
    // ДОПОЛНЕНИЕ ПЛАТФОРМЫ: реквизит-ссылка на «Адреса» — вид «Дополнение адреса»
    // (реестр, default_for; SSoT — сервер; kind = классификатор formFieldKind)
    return (
      <FieldShell key={code} label={label} labelStyle={fmtStyle} error={err} required={required}>
        <AddressField
          value={data[code] ?? ''}
          displayValue={String(data[code + '_presentation'] ?? '')}
          onChange={(v) => setValue({ [code]: v, [code + '_presentation']: '' })}
          disabled={fieldDisabled}
          picker
        />
      </FieldShell>
    );
  }
  if (kind === 'reference') {
    return (
      <FieldShell key={code} label={label} labelStyle={fmtStyle} error={err} required={required}>
        <ReferencePickerField
          value={data[code] ?? ''}
          refTable={refTable}
          types={(field as any).types as string[] | undefined}
          displayValue={String(data[code + '_presentation'] ?? '')}
          selectionMode={attrSelectModes[code] || ''}
          selectionParams={(field as any).selectionParams}
          selectionLinks={(field as any).selectionLinks}
          contextValues={data}
          disabled={fieldDisabled}
          onChange={(v) => setValue({ [code]: v })}
        />
      </FieldShell>
    );
  }

  // УНИВЕРСАЛЬНОЕ «поле с источником» (source_settings, SSoT — сервер):
  // автодополнение из внешних API + авто-заполнение формы по маппингу
  if (kind === 'source') {
    let source: SourceSettings | null = null;
    try { source = JSON.parse(sourceRaw); } catch { source = null; }
    if (source && source.operation) {
      return (
        <FieldShell key={code} label={label} labelStyle={fmtStyle} error={err} required={required}>
          <SourceField
            source={source}
            query={source.queryField ? String(data[source.queryField] ?? '') : undefined}
            disabled={fieldDisabled}
            placeholder={source.operation === 'suggest-address' ? 'Введите адрес для подбора…' : 'Введите ИНН или наименование…'}
            onPicked={(s, mapping) => {
              void (async () => {
                const updates: Record<string, unknown> = {};
                let resolvedAddressId = '';
                for (const m of mapping) {
                  if (m.from === 'address_id') {
                    if (!resolvedAddressId) {
                      try {
                        const r: any = await getSDK().geo.resolve({ suggestion: s.raw as any });
                        resolvedAddressId = r?.addressId || '';
                      } catch (e: any) { onSaveError(friendlyError(e)); return; }
                    }
                    updates[m.to] = resolvedAddressId;
                  } else {
                    updates[m.to] = (s.raw as any)[m.from];
                  }
                }
                if (Object.keys(updates).length > 0) {
                  onDataChange((prev) => ({ ...prev, ...updates }));
                }
              })();
            }}
          />
        </FieldShell>
      );
    }
  }
  const fieldMeta: FieldMeta = {
    name: code,
    label,
    data_type: dt,
    is_required: field.isRequired || field.is_required,
    is_read_only: false,
    ref_table: refTable,
    options: optionsOverride ?? (dt === 'enumeration' ? enumOptions[refTable] : undefined),
    voiceInput: (field as any).voiceInput === true || (field as any).voice_input === true,
    // Объект-родитель: правила default_for дополнений (object_type/object_code)
    parentType: objectType,
    parentCode: objectName,
  };
  return (
    <FormFieldRenderer
      key={code}
      field={fieldMeta}
      value={data[code] ?? ''}
      onChange={(v) => setValue({ [code]: v })}
      error={err}
      disabled={fieldDisabled}
      formValues={data}
      className={err ? '[&_input]:border-destructive' : ''}
    />
  );
}
